import { Delete, Get, Patch, Post } from "@/axios/AxiosFunctions";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/Core/Button";
import SearchInput from "@/components/Core/SearchInput";
import SideBarSkeleton from "@/components/Core/SideBarSkeleton";
import TableStructure from "@/components/Core/TableStructure";
import TableActionHandler from "@/components/TableActionHandler";
import { BaseURL } from "@/config/apiUrl";
import { recordsLimit } from "@/constant/constants";
import useDebounce from "@/customHooks/useDebounce";
import { warehouseManagementTableHeaders } from "@/data/data";
import { apiHeader, RenderToast } from "@/helper/HelperFunction";
import AddEditWareHouseModal from "@/modals/AddEditWareHouseModal";
import AreYouSureModal from "@/modals/AreYouSureModal";
import ViewWarehouseModal from "@/modals/ViewWarehouseModal";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./WarehouseManagement.module.css";

const MODAL_STATES = {
  WAREHOUSE_DETAILS: "WAREHOUSE_DETAILS",
  DELETE_WAREHOUSE: "DELETE_WAREHOUSE",
  CREATE_WAREHOUSE: "CREATE_WAREHOUSE",
  EDIT_WAREHOUSE: "EDIT_WAREHOUSE",
};

const WarehouseLoadingStates = {
  GET_WAREHOUSES: "GET_WAREHOUSES",
  DELETE_WAREHOUSE: "DELETE_WAREHOUSE",
  SUBMIT_WAREHOUSE: "SUBMIT_WAREHOUSE",
};

export default function WarehouseManagement() {
  const { access_token, user } = useSelector((state) => state.authReducer);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [showModal, setShowModal] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  // get warehouses
  const getData = async ({ _page = page, _search = debouncedSearch }) => {
    const queryParams = {
      page: _page,
      limit: recordsLimit,
      search: _search,
    };
    const apiUrl = BaseURL(
      `warehouses/admin/all?${new URLSearchParams(queryParams).toString()}`
    );
    setIsLoading(WarehouseLoadingStates.GET_WAREHOUSES);
    const response = await Get(apiUrl, access_token);
    if (response) {
      setData(response?.data?.data?.warehouses);
      setTotalRecord(response?.data?.data?.totalCount);
    }
    setIsLoading(null);
  };
  //   add edit warehouse
  const handleAddEditWarehouse = async (params) => {
    const apiUrl = BaseURL(`warehouses`);
    setIsLoading(WarehouseLoadingStates.SUBMIT_WAREHOUSE);
    const response = selectedData
      ? await Patch(apiUrl, params, apiHeader(access_token))
      : await Post(apiUrl, params, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: selectedData
          ? "Warehouse updated successfully"
          : "Warehouse added successfully",
      });
      if (selectedData) {
        setData(
          data?.map((item) => {
            if (item?._id === selectedData?._id) {
              return { ...item, ...params };
            }
            return item;
          })
        );
      } else {
        setData([response?.data?.data, ...data]);
      }
      setShowModal(null);
      setSelectedData(null);
    }
    setIsLoading(null);
  };
  //   delete warehouse
  const handleDeleteWarehouse = async () => {
    const apiUrl = BaseURL(`warehouses/${selectedData?._id}`);
    setIsLoading(WarehouseLoadingStates.DELETE_WAREHOUSE);
    const response = await Delete(apiUrl, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: "Warehouse deleted successfully",
      });
      setData(data?.filter((item) => item?._id !== selectedData?._id));
      setTotalRecord((prev) => prev - 1);
      setSelectedData(null);
      setShowModal(null);
    }
    setIsLoading(null);
  };

  useEffect(() => {
    getData({
      _page: 1,
    });
  }, [debouncedSearch]);

  return (
    <SideBarSkeleton>
      <div className={classes.mainWrapper}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <h3>Warehouse Management</h3>
            <Breadcrumb />
          </div>
        </div>
        <div className={classes.searchWrapper}>
          <SearchInput
            placeholder="Search Warehouse..."
            customStyle={{
              height: "40px",
            }}
            value={search}
            setter={setSearch}
          />
          <Button
            onClick={() => {
              if (user?.defaultState?.isDefault === true) {
                return RenderToast({
                  type: "error",
                  message: "Please select a state to create a warehouse",
                });
              }
              setShowModal(MODAL_STATES.CREATE_WAREHOUSE);
            }}
            variant="primary"
            label="Create Warehouse"
          />
        </div>

        <TableStructure
          headerTitle=""
          tableHeaders={warehouseManagementTableHeaders}
          isLoading={isLoading === WarehouseLoadingStates.GET_WAREHOUSES}
          tableContent={data?.map((item) => ({
            ...item,
            state: item?.state?.name,
            actions: (
              <TableActionHandler
                onView={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.WAREHOUSE_DETAILS);
                }}
                onEdit={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.EDIT_WAREHOUSE);
                }}
                onDelete={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.DELETE_WAREHOUSE);
                }}
              />
            ),
          }))}
          page={page}
          setPage={(e) => {
            setPage(e);
            getData({
              _page: e,
            });
          }}
          totalRecord={totalRecord}
          recordLimit={recordsLimit}
          noDataMessage="No Warehouses Found"
          customStyle={{
            height: "calc(100vh - 294px)",
          }}
        />
        {[MODAL_STATES.CREATE_WAREHOUSE, MODAL_STATES.EDIT_WAREHOUSE].includes(
          showModal
        ) && (
          <AddEditWareHouseModal
            show={[
              MODAL_STATES.CREATE_WAREHOUSE,
              MODAL_STATES.EDIT_WAREHOUSE,
            ].includes(showModal)}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            selectedItem={selectedData}
            handleClick={handleAddEditWarehouse}
            modalLoading={isLoading === WarehouseLoadingStates.SUBMIT_WAREHOUSE}
          />
        )}
        {showModal === MODAL_STATES.WAREHOUSE_DETAILS && (
          <ViewWarehouseModal
            show={showModal}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            data={selectedData}
          />
        )}
        {showModal === MODAL_STATES.DELETE_WAREHOUSE && (
          <AreYouSureModal
            show={showModal}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            title="Delete Warehouse"
            subTitle="Are you sure you want to delete this warehouse?"
            onClick={handleDeleteWarehouse}
            isApiCall={isLoading === WarehouseLoadingStates.DELETE_WAREHOUSE}
          />
        )}
      </div>
    </SideBarSkeleton>
  );
}
